import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, Transaction } from '@solana/web3.js'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { EmptyBlock, ErrorBlock, Panel, Spinner } from '~/components/ui'
import {
    createListing,
    fetchSellable,
    markSubmitted,
    Sellable,
    TradeError,
    TradeInfo,
    unstakeForListing,
} from '../api/trade'
import { CategoryTabs, SellControls, SellSort } from './SellFilters'
import { TradeDialog } from './TradeDialog'

/** Why an NFT cannot be listed, in words the seller can act on.
 *
 *  Keyed on the same codes `ensure_listable` raises, so this cannot drift from
 *  what the API will actually accept. Anything unrecognised falls back to a
 *  generic line rather than rendering a raw code. */
const REASONS: Record<string, string> = {
    staked: 'Unstake this first',
    in_live_game: 'In a game session',
    already_listed: 'Already listed',
    not_a_br1_asset: 'Not a BR1 collection',
    not_holder: 'Not in your wallet',
    burnt: 'Burnt',
}

const reasonText = (reason: string | null) =>
    (reason && REASONS[reason]) || 'Cannot be listed'

export const SellPanel = ({ info, onListed }: { info: TradeInfo | null; onListed: () => void }) => {
    const { publicKey, sendTransaction } = useWallet()
    const [items, setItems] = useState<Sellable[] | null>(null)
    const [counts, setCounts] = useState<Record<string, number>>({})
    const [total, setTotal] = useState(0)
    const [hasNext, setHasNext] = useState(false)
    const [page, setPage] = useState(1)
    const [loadError, setLoadError] = useState(false)
    const [selected, setSelected] = useState<Sellable | null>(null)
    const [price, setPrice] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [done, setDone] = useState<string | null>(null)
    const [unstaking, setUnstaking] = useState<string | null>(null)

    // Filter state. Every one of these is a server round trip, because an
    // account with 736 NFTs cannot have them all in the page to filter locally
    // -- the old flat `[:500]` silently hid 236 of them.
    const [category, setCategory] = useState('all')
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<SellSort>('id')
    const [listableOnly, setListableOnly] = useState(false)

    const load = (nextPage = 1, append = false) => {
        setLoadError(false)
        if (!append) setItems(null)

        fetchSellable({ page: nextPage, sort, category, search: search || undefined })
            .then((data) => {
                setItems((prev) => (append && prev ? [...prev, ...data.results] : data.results))
                setCounts(data.counts ?? {})
                setTotal(data.total ?? 0)
                setHasNext(Boolean(data.has_next))
                setPage(data.page ?? nextPage)
            })
            .catch(() => setLoadError(true))
    }

    useEffect(() => {
        load(1, false)
    }, [category, search, sort])

    /** Unstake without leaving the page.
     *
     *  Staking is a row in our database rather than an on-chain position, so
     *  this needs no signature. It is here because "unstake this first" was
     *  otherwise an instruction to go to another page and come back -- and
     *  anyone who stakes at all tends to stake everything, so it was the
     *  reason almost every card was greyed out. */
    const unstake = async (item: Sellable) => {
        setUnstaking(item.mint)
        setError(null)
        try {
            await unstakeForListing(item.mint)
            load(1, false)
        } catch {
            setError('Could not unstake that NFT. Try the Inventory page.')
        } finally {
            setUnstaking(null)
        }
    }

    const list = async () => {
        if (!selected || !publicKey) return
        setBusy(true)
        setError(null)
        setDone(null)

        try {
            const created = await createListing({
                mint: selected.mint,
                wallet: publicKey.toString(),
                price,
            })

            // The transaction arrives already partially signed by the escrow
            // wallet, which is also its fee payer -- the seller adds the owner
            // signature and needs no SOL of their own.
            const connection = new Connection(created.cluster_url, 'confirmed')
            const transaction = Transaction.from(Buffer.from(created.transaction, 'base64'))
            const signature = await sendTransaction(transaction, connection)

            // Reporting the signature moves the listing to "awaiting escrow"
            // and no further. It goes live only once the transfer is confirmed
            // on chain by the background task.
            await markSubmitted(created.listing.id, signature)

            setDone('Sent. Your listing goes live once the transfer confirms on chain.')
            setSelected(null)
            setPrice('')
            load()
            onListed()
        } catch (e) {
            setError(
                e instanceof TradeError
                    ? e.message
                    : 'The wallet did not complete the transfer. Nothing has been listed.'
            )
        } finally {
            setBusy(false)
        }
    }

    if (loadError) return <ErrorBlock onRetry={() => load(1, false)} />

    // Applied to the fetched page rather than sent to the server: the tab
    // counts mean "what you hold", and filtering server-side would make them
    // disagree with the grid beneath them.
    const visible = (items ?? []).filter((i) => !listableOnly || i.listable)

    const priceValid =
        price !== '' &&
        !Number.isNaN(Number(price)) &&
        Number(price) > 0 &&
        (!info || (Number(price) >= Number(info.min_price) && Number(price) <= Number(info.max_price)))

    const needsReview = info && price !== '' && Number(price) >= Number(info.review_threshold)

    /** Why "Approve listing" cannot be pressed, in words, or null if it can.
     *
     *  The button used to be `disabled={busy || !priceValid || !publicKey}` and
     *  said nothing about which of those was true. A dead control with no
     *  explanation is not a validation state, it is a puzzle: the reported
     *  symptom was "the approve button is never clickable", from someone who
     *  had neither connected a wallet nor typed a price and had no way to know
     *  either mattered.
     *
     *  It also now accounts for escrow being unavailable. Previously the button
     *  went live on a valid price even when the server was refusing every
     *  listing, so the only way to discover that was to press it and read the
     *  error. */
    const blocker = (): string | null => {
        if (info && !info.escrow_available)
            return 'The marketplace is not accepting new listings right now.'
        if (!publicKey) return 'Connect the wallet that holds this NFT.'
        if (price === '') return 'Enter a price.'
        if (Number.isNaN(Number(price))) return 'That is not a number.'
        if (Number(price) <= 0) return 'The price must be more than zero.'
        if (info && Number(price) < Number(info.min_price))
            return `The lowest price allowed is $${info.min_price}.`
        if (info && Number(price) > Number(info.max_price))
            return `The highest price allowed is $${info.max_price}.`
        return null
    }
    const blockedBecause = blocker()

    return (
        <div className='flex flex-col gap-5'>
            {!publicKey && (
                <Panel className='p-4 text-sm text-white/70'>
                    Connect the wallet that holds the NFT to list it.
                </Panel>
            )}

            {done && (
                <Panel className='p-4 text-sm text-[#00FF90]' border='rgba(0,255,144,0.35)'>
                    {done}
                </Panel>
            )}

            <CategoryTabs counts={counts} value={category} onChange={setCategory} />

            <SellControls
                search={search}
                onSearch={setSearch}
                sort={sort}
                onSort={setSort}
                listableOnly={listableOnly}
                onListableOnly={setListableOnly}
            />

            {items === null ? (
                <div className='flex justify-center py-16'>
                    <Spinner visible={true} />
                </div>
            ) : visible.length === 0 ? (
                <EmptyBlock title='Nothing here'>
                    {total === 0
                        ? 'You do not hold any BR1 NFTs.'
                        : 'No NFTs match those filters.'}
                </EmptyBlock>
            ) : (
                <>
                    <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
                        {visible.map((item) => (
                            <div
                                key={item.mint}
                                className={twMerge(
                                    'relative flex flex-col border p-2 text-left transition-colors',
                                    selected?.mint === item.mint
                                        ? 'border-[#03CEFF] bg-white/[0.06]'
                                        : 'border-white/10'
                                )}
                            >
                                <button
                                    disabled={!item.listable}
                                    onClick={() => {
                                        setSelected(item)
                                        setError(null)
                                        setDone(null)
                                    }}
                                    className={twMerge(
                                        'text-left',
                                        !item.listable && 'cursor-not-allowed opacity-40'
                                    )}
                                >
                                    <div className='aspect-square w-full overflow-hidden bg-white/[0.03]'>
                                        {item.image_url && (
                                            <img
                                                src={item.image_url}
                                                alt={item.name}
                                                loading='lazy'
                                                className='h-full w-full object-contain'
                                            />
                                        )}
                                    </div>
                                    <p className='mt-2 truncate text-xs font-bold text-white'>
                                        {item.name}
                                    </p>
                                    {!item.listable && (
                                        <p className='truncate text-[10px] uppercase tracking-widest text-white/40'>
                                            {reasonText(item.reason)}
                                        </p>
                                    )}
                                </button>

                                {/* The one blocker the seller can clear from
                                    here. Every other reason needs something
                                    outside this page, so only this one gets a
                                    button. */}
                                {item.reason === 'staked' && (
                                    <button
                                        onClick={() => unstake(item)}
                                        disabled={unstaking === item.mint}
                                        className='mt-2 min-h-[2rem] border border-[#03CEFF]/50 px-2 text-[10px] font-black uppercase tracking-widest text-[#03CEFF] transition-colors hover:bg-[#03CEFF]/10 disabled:opacity-40'
                                    >
                                        {unstaking === item.mint ? 'Unstaking…' : 'Unstake to sell'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {hasNext && !listableOnly && (
                        <button
                            onClick={() => load(page + 1, true)}
                            className='min-h-[2.75rem] self-center border border-white/25 px-8 text-xs font-black uppercase tracking-[0.2em] text-white hover:border-white/50'
                        >
                            Load more ({items.length} of {total})
                        </button>
                    )}
                </>
            )}

            <TradeDialog
                open={selected !== null}
                title={selected ? `List ${selected.name}` : ''}
                busy={busy}
                onClose={() => {
                    setSelected(null)
                    setError(null)
                }}
            >
                {selected && (
                    <div className='flex flex-col gap-3'>
                        {/* The item restated inside the dialog. The grid is behind
                            the overlay, so without this nothing on screen confirms
                            WHICH of several hundred NFTs is about to leave the
                            wallet. */}
                        <div className='flex flex-row items-center gap-3 border border-white/10 p-2'>
                            <div className='h-16 w-16 shrink-0 overflow-hidden bg-white/[0.03]'>
                                {selected.image_url && (
                                    <img
                                        src={selected.image_url}
                                        alt={selected.name}
                                        className='h-full w-full object-contain'
                                    />
                                )}
                            </div>
                            <div className='min-w-0'>
                                <p className='truncate text-sm font-bold text-white'>{selected.name}</p>
                                <p className='truncate text-[10px] uppercase tracking-widest text-white/40'>
                                    {selected.operative_type}
                                </p>
                            </div>
                        </div>

                        <label className='flex flex-col gap-1 text-[11px] uppercase tracking-widest text-white/40'>
                            Price in USD
                            <input
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                inputMode='decimal'
                                placeholder='0.00'
                                className='w-full border border-white/15 bg-black/40 px-3 py-2 text-base font-bold normal-case tracking-normal text-white outline-none focus:border-[#03CEFF]'
                            />
                        </label>

                        {info && (
                            <p className='text-xs text-white/50'>
                                We take {info.fee_percent}%. You receive{' '}
                                <span className='font-bold text-white'>
                                    $
                                    {priceValid
                                        ? (Number(price) * (1 - Number(info.fee_percent) / 100)).toFixed(2)
                                        : '0.00'}
                                </span>{' '}
                                when it sells.
                            </p>
                        )}

                        {needsReview && (
                            <p className='text-xs text-[#FFC53D]'>
                                Listings at ${info?.review_threshold} or more are reviewed by the team
                                before they go live.
                            </p>
                        )}

                        <p className='text-xs text-white/40'>
                            The NFT leaves your wallet and is held by BR1 until it sells or you cancel.
                            You do not need any SOL — we cover the network fee.
                        </p>

                        {error && <p className='text-xs text-[#FF6B6B]'>{error}</p>}

                        {/* Say why, every time it is off. The reason sits above
                            the button rather than in a tooltip because a
                            disabled control does not fire hover on touch, and
                            this page is used on phones. */}
                        {blockedBecause && !busy && (
                            <p id='listing-blocked' className='text-xs text-[#FFC53D]'>
                                {blockedBecause}
                            </p>
                        )}

                        <button
                            onClick={list}
                            disabled={busy || blockedBecause !== null}
                            aria-describedby={blockedBecause ? 'listing-blocked' : undefined}
                            className='min-h-[2.75rem] bg-[#03CEFF] px-6 text-xs font-black uppercase tracking-[0.2em] text-black transition-[filter] hover:brightness-110 disabled:opacity-40 disabled:hover:brightness-100'
                        >
                            {busy ? 'Waiting for your wallet…' : 'Approve listing'}
                        </button>
                    </div>
                )}
            </TradeDialog>
        </div>
    )
}
